import { useMutation } from '@tanstack/react-query'
import { useApi } from '@/lib/api-context'

/**
 * A confirmação pelo documento (docs/api/confirmacao-pelo-documento.md; T58, T59): o documento traz
 * os e-mails mascarados dos cadastros com ele; a escolha manda o código; o código confirma. O mesmo
 * contrato em Entrar (`/portal/sign-in`) e no cadastro pelo link (`/registration/identity?t=`). A
 * página nunca chama `fetch`.
 */

export type IdentityDocType = 'cpf' | 'passport' | 'rne' | 'crnm' | 'other'

export interface IdentityDocument {
  type: IdentityDocType
  number: string
  country?: string
}

export interface EmailOption {
  id: string
  /** "a•••@g•••.com": o endereço nunca vem inteiro. */
  masked: string
}

export interface IdentityOptions {
  emails: EmailOption[]
  message: string
}

export interface CodeSent {
  message: string
  expiresInMinutes: number
}

/** Os dados pessoais que o preenchimento rápido traz (RN-PAC-54). */
export interface Prefill {
  name: string
  birthDate: string | null
  docType: 'cpf' | null
  docNumber: string | null
  foreignDocument: { type: Exclude<IdentityDocType, 'cpf'>; number: string; country: string } | null
  rg: string | null
  gender: 'female' | 'male' | 'other' | 'not_provided'
  maritalStatus:
    'single' | 'married' | 'domestic_partnership' | 'separated' | 'divorced' | 'widowed' | null
  occupation: string | null
  email: string
  whatsapp: string | null
  address: {
    postalCode: string | null
    street: string | null
    number: string | null
    complement: string | null
    district: string | null
    city: string | null
    state: string | null
    country: string
  } | null
}

export interface Prefilled {
  prefillId: string
  prefill: Prefill
}

export interface EmailChanged {
  emailGrantId: string
  email: string
}

/** Os caminhos de um lugar: a lista, o código e a confirmação (e, no cadastro, a troca de e-mail). */
export interface IdentityPaths {
  lookup: string
  code: string
  verify: string
  email?: string
  emailVerify?: string
}

/** Os caminhos do cadastro pelo link, com o token em `?t=`. */
export function registrationPaths(token: string): Required<IdentityPaths> {
  const t = `?t=${encodeURIComponent(token)}`
  return {
    lookup: `/registration/identity${t}`,
    code: `/registration/identity/code${t}`,
    verify: `/registration/identity/verify${t}`,
    email: `/registration/identity/email${t}`,
    emailVerify: `/registration/identity/email/verify${t}`,
  }
}

/** Os caminhos de Entrar no portal (RN-POR-19). */
export const SIGN_IN_PATHS: IdentityPaths = {
  lookup: '/portal/sign-in',
  code: '/portal/sign-in/code',
  verify: '/portal/sign-in/verify',
}

export function useIdentityLookup(paths: IdentityPaths) {
  const api = useApi()
  return useMutation({
    mutationFn: (document: IdentityDocument) =>
      api.post<IdentityOptions>(paths.lookup, { document }),
  })
}

export function useIdentityCode(paths: IdentityPaths) {
  const api = useApi()
  return useMutation({
    mutationFn: (input: { document: IdentityDocument; emailId: string }) =>
      api.post<CodeSent>(paths.code, input),
  })
}

export function useIdentityVerify<T>(paths: IdentityPaths) {
  const api = useApi()
  return useMutation({
    mutationFn: (input: { document: IdentityDocument; emailId: string; code: string }) =>
      api.post<T>(paths.verify, input),
  })
}

export function useEmailChangeCode(paths: IdentityPaths) {
  const api = useApi()
  return useMutation({
    mutationFn: (input: { document: IdentityDocument; prefillId: string; email: string }) =>
      api.post<CodeSent>(paths.email ?? '', input),
  })
}

export function useEmailChangeVerify(paths: IdentityPaths) {
  const api = useApi()
  return useMutation({
    mutationFn: (input: {
      document: IdentityDocument
      prefillId: string
      email: string
      code: string
    }) => api.post<EmailChanged>(paths.emailVerify ?? '', input),
  })
}
